import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import {
  Box,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';

// Helper to determine the dynamic table rows based on selection
function getDynamicCategories(
  selectedBusinessType: string,
  selectedActivities: string[]
) {
  const rows: {
    category: string;
    formTitle: string;
    description: string;
    deadline?: string;
  }[] = [];
  const has = (activity: string) => selectedActivities.includes(activity);

  // 1. Business Tax Form / Entity Return (Your main return)
  if (selectedBusinessType === 'C Corporation') {
    rows.push({
      category: 'Your main return',
      formTitle: 'Form 1120',
      description: 'Reports corporate income, deductions, and tax.',
      deadline: 'April 15 fiscal year: 15th day of 4th month',
    });
  } else if (selectedBusinessType === 'S Corporation') {
    rows.push(
      {
        category: 'Your main return',
        formTitle: 'Form 1120-S',
        description:
          'Reports S corporation income, deductions, and other tax items.',
        deadline: 'March 15 · fiscal year: 15th day of 3rd month',
      },
      {
        category: 'Your main return',
        formTitle: 'Schedule K-1 (Form 1120-S)',
        description:
          "Shareholder's share of S corporation income and other items.",
        deadline: 'Provided with your return',
      }
    );
  } else if (selectedBusinessType === 'Partnership') {
    rows.push(
      {
        category: 'Your main return',
        formTitle: 'Form 1065',
        description:
          'Reports partnership income, deductions, and partner allocations.',
        deadline: 'March 15 · fiscal year: 15th day of 3rd month',
      },
      {
        category: 'Your main return',
        formTitle: 'Schedule K-1 (Form 1065)',
        description: "Partner's share of partnership income and other items.",
        deadline: 'Provided with your return',
      }
    );
  } else if (selectedBusinessType === 'Tax-Exempt Organization') {
    rows.push({
      category: 'Your main return',
      formTitle: 'Form 990-T',
      description: 'Reports unrelated business income and applicable tax.',
      deadline:
        'April 15 or May 15 · fiscal year: 15th day of 4th or 5th month',
    });
  } else if (selectedBusinessType === 'Political Organization') {
    rows.push({
      category: 'Your main return',
      formTitle: 'Form 1120-POL',
      description: 'Reports taxable political organization income.',
      deadline: 'April 15 · fiscal year: 15th day of 4th month',
    });
  }

  // 2. Activity-based returns
  if (has('Pays employees')) {
    if (
      !has('Has been instructed by the IRS to file employment taxes annually')
    ) {
      rows.push({
        category: 'Other forms for your activities',
        formTitle: 'Form 941',
        description:
          'Reports federal income tax withheld, and both the employer and employee portions of Social Security and Medicare taxes.',
        deadline: 'Q1: Apr 30 · Q2: Jul 31 · Q3: Oct 31 · Q4: Feb 1',
      });
    }
    rows.push(
      {
        category: 'Other forms for your activities',
        formTitle: 'Form 940',
        description: 'Reports federal unemployment tax.',
        deadline:
          'February 1 (February 10 if all FUTA tax was deposited on time)',
      },
      {
        category: 'Other forms for your activities',
        formTitle: 'Form W-2',
        description:
          'Reports employee wages, salaries, tips, and federal income tax withheld during the tax year.',
        deadline: 'February 1',
      }
    );
  }

  if (has('Has been instructed by the IRS to file employment taxes annually')) {
    rows.push({
      category: 'Other forms for your activities',
      formTitle: 'Form 944',
      description:
        'Annual employment tax return for small employers with $1,000 or less in annual employment tax liability, as instructed by the IRS.',
      deadline: 'February 1',
    });
  }

  if (has('Pays agricultural or farm workers')) {
    rows.push({
      category: 'Other forms for your activities',
      formTitle: 'Form 943',
      description: 'Reports employment taxes for agricultural employees.',
      deadline: 'February 1',
    });
  }

  if (has('Pays independent contractors or vendors')) {
    rows.push(
      {
        category: 'Other forms for your activities',
        formTitle: 'Form 1099-NEC',
        description:
          'Reports nonemployee compensation of $2,000 or more for 2026, subject to IRS reporting rules.',
        deadline: 'January 31 (recipients and IRS)',
      },
      {
        category: 'Other forms for your activities',
        formTitle: 'Form W-9',
        description:
          'Collects from contractors and vendors for their taxpayer ID. You keep it; it is not filed with the IRS.',
      }
    );
  }

  if (has('Makes payments to non-U.S. individuals')) {
    rows.push({
      category: 'Other forms for your activities',
      formTitle: 'Form W-8BEN',
      description:
        'Documents a non-U.S. individual’s foreign status and supports treaty benefit claims. You keep it; it is not filed with the IRS.',
    });
  }

  if (has('Withholds federal income tax from nonpayroll payments')) {
    rows.push({
      category: 'Other forms for your activities',
      formTitle: 'Form 945',
      description:
        'Reports backup withholding and other nonpayroll withholding.',
      deadline: 'February 1',
    });
  }

  if (has('Needs to request a tax filing extension')) {
    if (!selectedBusinessType) {
      rows.push({
        category: 'Extension form',
        formTitle: 'Form 7004 or Form 8868',
        description:
          'Choose your business type to see whether Form 7004 or Form 8868 applies.',
      });
    } else {
      const isExempt = selectedBusinessType === 'Tax-Exempt Organization';
      rows.push({
        category: 'Extension form',
        formTitle: isExempt ? 'Form 8868' : 'Form 7004',
        description: isExempt
          ? 'Application for Automatic Extension of Time To File an Exempt Organization Return.'
          : 'Application for Automatic Extension of Time To File Certain Business Income Tax, Information, and Other Returns.',
        deadline: isExempt
          ? 'Due on or before the regular due date of the return (May 15 / April 15)'
          : 'Due on or before the regular due date of the applicable return',
      });
    }
  }

  return rows;
}

export default function FormFinder() {
  const [selectedBusinessType, setSelectedBusinessType] =
    useState<string>('C Corporation');
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);

  const handleBusinessTypeClick = (type: string) => {
    setSelectedBusinessType((prev) => (prev === type ? '' : type));
  };

  const handleActivityClick = (activity: string) => {
    setSelectedActivities((prev) =>
      prev.includes(activity)
        ? prev.filter((item) => item !== activity)
        : [...prev, activity]
    );
  };

  const dynamicRows = getDynamicCategories(
    selectedBusinessType,
    selectedActivities
  );

  const showGoodToKnow =
    selectedActivities.includes('Pays employees') ||
    selectedActivities.includes(
      'Has been instructed by the IRS to file employment taxes annually'
    );

  return (
    <Box
      component="section"
      id="form-finder"
      aria-labelledby="form-finder-heading"
      sx={{
        width: '100%',
        mt: 3,
        py: { xs: 6, md: 6 },
        px: { xs: 2, sm: 3, md: 4 },
        backgroundColor: '#fafcff',
        borderTop: '1px solid #e0e0e0',
        backgroundImage: `
          linear-gradient(to bottom, #ffffff 0%, transparent 20%, transparent 80%, #ffffff 100%),
          radial-gradient(circle at 25% 20%, rgba(74, 38, 192, 0.06) 0%, transparent 45%),
          radial-gradient(circle at 75% 80%, rgba(34, 61, 153, 0.07) 0%, transparent 45%),
          radial-gradient(rgba(34, 61, 153, 0.18) 1.5px, transparent 1.6px)
        `,
        backgroundSize: '100% 100%, 100% 100%, 100% 100%, 24px 24px',
        backgroundRepeat: 'no-repeat, no-repeat, no-repeat, repeat',
      }}
    >
      <Container maxWidth="xl">
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              borderRadius: '100px',
              backgroundColor: '#e3edff',
              color: '#213e99',
              fontWeight: 600,
              fontSize: '14px',
              mb: 2,
            }}
          >
            <FactCheckOutlinedIcon fontSize="small" />
            Accurate Form Matching
          </Box>
          <Typography
            variant="h2"
            id="form-finder-heading"
            sx={{
              fontSize: { xs: 30, sm: 36, md: 44 },
              fontWeight: 700,
              mb: 2,
            }}
          >
            Form Finder Tool
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: 16, sm: 18, md: 20 },
              maxWidth: 780,
              mx: 'auto',
            }}
          >
            Answer two quick questions to find the main tax forms and filing
            deadlines that may apply to your business.
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 4, lg: 5 }} alignItems="flex-start">
          {/* Left side: Questions */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2.5, sm: 4 },
                borderRadius: 4,
                borderColor: '#e1d8eb',
                backgroundColor: '#ffffff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
              }}
            >
              {/* Question 1: What is your business type? */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: 18, sm: 20 },
                    fontWeight: 700,
                    mb: 2,
                    textAlign: 'left',
                  }}
                >
                  1. What is your business type?
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.25,
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => handleBusinessTypeClick('C Corporation')}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor:
                        selectedBusinessType === 'C Corporation'
                          ? '#f0eeff'
                          : '#ffffff',
                      border:
                        selectedBusinessType === 'C Corporation'
                          ? '1.5px solid #213e99'
                          : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    C Corporation
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() => handleBusinessTypeClick('S Corporation')}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor:
                        selectedBusinessType === 'S Corporation'
                          ? '#f0eeff'
                          : '#ffffff',
                      border:
                        selectedBusinessType === 'S Corporation'
                          ? '1.5px solid #213e99'
                          : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    S Corporation
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() => handleBusinessTypeClick('Partnership')}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor:
                        selectedBusinessType === 'Partnership'
                          ? '#f0eeff'
                          : '#ffffff',
                      border:
                        selectedBusinessType === 'Partnership'
                          ? '1.5px solid #213e99'
                          : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Partnership
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      handleBusinessTypeClick('Tax-Exempt Organization')
                    }
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor:
                        selectedBusinessType === 'Tax-Exempt Organization'
                          ? '#f0eeff'
                          : '#ffffff',
                      border:
                        selectedBusinessType === 'Tax-Exempt Organization'
                          ? '1.5px solid #213e99'
                          : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Tax-Exempt Organization
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      handleBusinessTypeClick('Political Organization')
                    }
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor:
                        selectedBusinessType === 'Political Organization'
                          ? '#f0eeff'
                          : '#ffffff',
                      border:
                        selectedBusinessType === 'Political Organization'
                          ? '1.5px solid #213e99'
                          : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Political Organization
                  </Box>
                </Box>
              </Box>

              {/* Question 2: What does your business do? */}
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: 18, sm: 20 },
                    fontWeight: 700,
                    mb: 2,
                    textAlign: 'left',
                  }}
                >
                  2. What does your business do?{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ fontWeight: 400, color: '#64748b' }}
                  >
                    Select that applies to you
                  </Typography>
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.25,
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => handleActivityClick('Pays employees')}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor: selectedActivities.includes(
                        'Pays employees'
                      )
                        ? '#f0eeff'
                        : '#ffffff',
                      border: selectedActivities.includes('Pays employees')
                        ? '1.5px solid #213e99'
                        : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Pays employees
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      handleActivityClick(
                        'Has been instructed by the IRS to file employment taxes annually'
                      )
                    }
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor: selectedActivities.includes(
                        'Has been instructed by the IRS to file employment taxes annually'
                      )
                        ? '#f0eeff'
                        : '#ffffff',
                      border: selectedActivities.includes(
                        'Has been instructed by the IRS to file employment taxes annually'
                      )
                        ? '1.5px solid #213e99'
                        : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Has been instructed by the IRS to file employment taxes
                    annually
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      handleActivityClick('Pays agricultural or farm workers')
                    }
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor: selectedActivities.includes(
                        'Pays agricultural or farm workers'
                      )
                        ? '#f0eeff'
                        : '#ffffff',
                      border: selectedActivities.includes(
                        'Pays agricultural or farm workers'
                      )
                        ? '1.5px solid #213e99'
                        : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Pays agricultural or farm workers
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      handleActivityClick(
                        'Pays independent contractors or vendors'
                      )
                    }
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor: selectedActivities.includes(
                        'Pays independent contractors or vendors'
                      )
                        ? '#f0eeff'
                        : '#ffffff',
                      border: selectedActivities.includes(
                        'Pays independent contractors or vendors'
                      )
                        ? '1.5px solid #213e99'
                        : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Pays independent contractors or vendors
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      handleActivityClick(
                        'Makes payments to non-U.S. individuals'
                      )
                    }
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor: selectedActivities.includes(
                        'Makes payments to non-U.S. individuals'
                      )
                        ? '#f0eeff'
                        : '#ffffff',
                      border: selectedActivities.includes(
                        'Makes payments to non-U.S. individuals'
                      )
                        ? '1.5px solid #213e99'
                        : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Makes payments to non-U.S. individuals
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      handleActivityClick(
                        'Withholds federal income tax from nonpayroll payments'
                      )
                    }
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor: selectedActivities.includes(
                        'Withholds federal income tax from nonpayroll payments'
                      )
                        ? '#f0eeff'
                        : '#ffffff',
                      border: selectedActivities.includes(
                        'Withholds federal income tax from nonpayroll payments'
                      )
                        ? '1.5px solid #213e99'
                        : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Withholds federal income tax from nonpayroll payments
                  </Box>

                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      handleActivityClick(
                        'Needs to request a tax filing extension'
                      )
                    }
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2.5,
                      py: 1.2,
                      backgroundColor: selectedActivities.includes(
                        'Needs to request a tax filing extension'
                      )
                        ? '#f0eeff'
                        : '#ffffff',
                      border: selectedActivities.includes(
                        'Needs to request a tax filing extension'
                      )
                        ? '1.5px solid #213e99'
                        : '1px solid #dcd8ea',
                      borderRadius: '100px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      fontWeight: 600,
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#213e99',
                        backgroundColor: '#f6f4fe',
                      },
                    }}
                  >
                    Needs to request a tax filing extension
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right side: Forms that may apply to you with scrollable container */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Paper
              variant="outlined"
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 100 },
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 4,
                borderColor: '#e1d8eb',
                backgroundColor: '#ffffff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 20, sm: 22 },
                  fontWeight: 700,
                  mb: 2.5,
                  textAlign: 'left',
                }}
              >
                Forms that may apply to you
              </Typography>

              {/* Fixed height container with scroll and custom scrollbar */}
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: '#e1d8eb',
                  maxHeight: { xs: 400, md: 420 },
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f5f9',
                    borderRadius: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#cbd5e1',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: '#94a3b8',
                    },
                  },
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          width: '38%',
                          backgroundColor: '#f4f0ff',
                          borderBottom: '1px solid #e1d8eb',
                        }}
                      >
                        Category
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          backgroundColor: '#f4f0ff',
                          borderBottom: '1px solid #e1d8eb',
                        }}
                      >
                        Form & Filing Details
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dynamicRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ py: 3, textAlign: 'center', color: '#64748b' }}
                        >
                          Select business type or activities to see applicable
                          forms.
                        </TableCell>
                      </TableRow>
                    ) : (
                      dynamicRows.map((row, index) => (
                        <TableRow
                          key={`${row.formTitle}-${index}`}
                          sx={{
                            backgroundColor:
                              index % 2 === 1 ? '#fafcff' : '#ffffff',
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              verticalAlign: 'top',
                              color: '#1e293b',
                            }}
                          >
                            {row.category}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: '#213e99',
                                fontSize: '15px',
                                mb: 0.5,
                              }}
                            >
                              {row.formTitle}
                            </Typography>
                            <Typography
                              sx={{
                                color: '#475569',
                                fontSize: '13.5px',
                                mb: row.deadline ? 0.75 : 0,
                                lineHeight: 1.45,
                              }}
                            >
                              {row.description}
                            </Typography>
                            {row.deadline && (
                              <Box
                                sx={{
                                  fontSize: '12.5px',
                                  color: '#0f172a',
                                  fontWeight: 500,
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '12.5px',
                                    color: '#213e99',
                                  }}
                                >
                                  Due:{' '}
                                </Typography>
                                {row.deadline}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {showGoodToKnow && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: '12px 16px',
                    borderRadius: 3,
                    backgroundColor: 'rgba(33, 62, 153, 0.05)',
                    border: '1px solid #e1d8eb',
                    borderLeft: '4px solid #213e99',
                    color: '#475569',
                    fontSize: '13.5px',
                    lineHeight: 1.5,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: '#0f172a',
                      fontSize: '13.5px',
                    }}
                  >
                    Good to know:{' '}
                  </Typography>
                  Employers can’t simply choose between Form 941 and Form 944.
                  The IRS determines who qualifies or is instructed to file Form
                  944.
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
